<template>
   <div id="home-main-container" class="bg-white q-pa-md">
      <!-- Header Section -->
      <PageHeader title="Combination Explorer" />

      <div id="main-container">
         <!-- Installation Instructions or Stacked Graphs -->
         <StackedGraphs2
            :traffic-graph-id="'traffic-graph'"
            :conversion-graph-id="'conversion-graph'"
            :time-series="analyticsStore.timeSeries ?? null"
            :conversion-time-series="
               analyticsStore.conversionTimeSeries ?? null
            "
            :loading="loading"
         />
         <CombinationsTable
            id="combinations-table"
            :top-n="analyticsStore?.topN ?? null"
            :stats="undefined"
            :days-back="daysBack"
            :has-variants="!!analyticsStore.topN"
            :loading="loading"
            @get-combos="handleGetCombos"
         />
      </div>
   </div>
</template>
<script setup lang="ts">
// Imports
import { useQuasar } from "quasar"
import { useProjectStore } from "../stores"
import { useAnalyticsStore } from "@stores/analyticsStore"
import { onMounted, watch, ref, computed, toRefs } from "vue"
import StackedGraphs2 from "@components/combination-explorer/StackedGraphs.vue"
import PageHeader from "@/components/common/PageHeader.vue"
import CombinationsTable from "../components/combination-explorer/combinations-table/CombinationsTable.vue"
import { useTour } from "@/composables/useTour"
import { combinationExplorerTour } from "@/tours/combinationExplorerTour"
// Utilities
const $q = useQuasar()

// Stores
const projectStore = useProjectStore()
const analyticsStore = useAnalyticsStore()
const { start_date, end_date, utmSource } = toRefs(analyticsStore)
const loading = ref(false)

import { TablePagination } from "@/types/combinations-table"
import { callWithLoading } from "@utils/callWithLoading"

const pagination = ref<TablePagination>({
   page: 1,
   rowsPerPage: 10,
   sortBy: "traffic_pct",
   descending: true,
})

const daysBack = computed(() => {
   return Math.floor(
      (end_date.value - start_date.value) / (1000 * 60 * 60 * 24),
   )
})

const handleGetComboExplorer = async (newPagination: TablePagination) => {
   pagination.value = newPagination
   const startAt = newPagination.rowsPerPage * (newPagination.page - 1)

   await analyticsStore.fetchCombinationExplorer({
      startAt,
      rowsPerPage: newPagination.rowsPerPage,
      sortBy: newPagination.sortBy,
      descending: newPagination.descending,
   })
}

const handleGetCombos = async (newPagination: TablePagination) => {
   pagination.value = newPagination
   const startAt = newPagination.rowsPerPage * (newPagination.page - 1)

   await analyticsStore.fetchTopN({
      startAt,
      rowsPerPage: newPagination.rowsPerPage,
      sortBy: newPagination.sortBy,
      descending: newPagination.descending,
   })
}

// Tour initialization
const { autoStartTour } = useTour(combinationExplorerTour)

// Lifecycle Hooks
onMounted(async () => {
   try {
      $q.loadingBar.start()
      loading.value = true
      await projectStore.getVariablesByProjectId()
      $q.loadingBar.increment(50)
      await handleGetComboExplorer({
         ...pagination.value,
         page: 1,
      })

      if (analyticsStore.topN) {
         loading.value = false
         await autoStartTour()
      }
   } catch (error) {
      $q.loadingBar.stop()
      throw error
   } finally {
      $q.loadingBar.stop()
   }
})

// Watch for UTM source changes
watch(utmSource, async () => {
   loading.value = true
   await callWithLoading(handleGetComboExplorer, $q, {
      ...pagination.value,
      page: 1,
   })
   if (analyticsStore.topN) {
      loading.value = false
   }
})

// Watch for date changes in the store
watch(
   () => [analyticsStore.start_date, analyticsStore.end_date],
   async () => {
      loading.value = true
      await callWithLoading(handleGetComboExplorer, $q, {
         ...pagination.value,
         page: 1,
      })
      if (analyticsStore.topN) {
         loading.value = false
      }
   },
)
</script>
<style lang="scss" scoped>
#main-container {
   width: 100%;
}
#kpi-container {
   display: flex;
   align-items: center;
   gap: 2em;
   width: 100%;
   padding-top: 1em;
   padding-bottom: 1em;
}
.kpi-card {
   width: 100%;
}
#graphs-container {
   width: 100%;
   display: flex;
   flex-direction: row;
   align-items: center;
   gap: 2em;
}
.graph-card {
   width: 100%;
}
#chips-container {
   display: flex;
   gap: 1em;
}
#conversion-rate-filter-container {
   display: flex;
   gap: 1em;
   // border: 1px solid rgb(160, 160, 160);
   // border-radius: 5px;
   padding: 1em 2em;
}
@media (max-width: 1400px) {
   #dashboard-title {
      margin-bottom: 0.5em;
   }
   .graph-card {
      width: 100%;
   }
   #kpi-container {
      display: grid;
      grid-template-columns: 1fr 1fr;
   }
   #graphs-container {
      flex-direction: column;
   }
   #dropdown-container {
      margin-bottom: 1em;
   }
   #chips-container {
      width: 100%;
   }
}
#home-main-container {
   min-height: 100vh;
}
#intro-paragraph {
   max-width: 800px;
}
</style>
